if (!customElements.get('rb-slider-progress')) {
  class RbSliderProgress extends HTMLElement {
    connectedCallback() {
      if (this.slider) return;

      this.slider = this.closest('slider-component');
      if (!this.slider) return;

      this.currentElement = this.slider.querySelector('.slider-counter--current');
      this.totalElement = this.slider.querySelector('.slider-counter--total');
      this.handleSlideChange = (event) => this.update(event.detail.currentPage);
      this.slider.addEventListener('slideChanged', this.handleSlideChange);

      if (this.totalElement) {
        this.totalObserver = new MutationObserver(() => this.update());
        this.totalObserver.observe(this.totalElement, { childList: true });
      }

      requestAnimationFrame(() => this.update());
    }

    disconnectedCallback() {
      this.slider?.removeEventListener('slideChanged', this.handleSlideChange);
      this.totalObserver?.disconnect();
      this.slider = null;
      this.totalObserver = null;
    }

    update(currentPage) {
      const current = currentPage || Number(this.currentElement?.textContent) || 1;
      const total = Number(this.totalElement?.textContent) || 1;
      this.style.setProperty('--rb-progress', `${Math.min(current / total, 1) * 100}%`);
    }
  }

  customElements.define('rb-slider-progress', RbSliderProgress);
}


if (!customElements.get('rb-slider-autoplay')) {
  class RbSliderAutoplay extends HTMLElement {
    connectedCallback() {
      if (this.initialized || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.sliderComponent = this.querySelector('slider-component');
      this.slider = this.sliderComponent?.querySelector('[id^="Slider-"]');
      this.nextButton = this.sliderComponent?.querySelector('button[name="next"]');
      if (!this.slider || !this.nextButton) return;

      this.initialized = true;
      this.delay = Math.max(Number(this.dataset.delay) || 5000, 3000);
      this.visible = false;
      this.pause = () => {
        this.interacting = true;
        this.stop();
      };
      this.resume = () => {
        this.interacting = false;
        this.start();
      };
      this.handleFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.resume();
      };
      this.handleVisibility = () => (document.hidden ? this.stop() : this.start());

      this.addEventListener('mouseenter', this.pause);
      this.addEventListener('mouseleave', this.resume);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('touchstart', this.pause, { passive: true });
      this.addEventListener('touchend', this.resume, { passive: true });
      document.addEventListener('visibilitychange', this.handleVisibility);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.visible ? this.start() : this.stop();
        }, { threshold: 0.2 });
        this.observer.observe(this);
      } else {
        this.visible = true;
        this.start();
      }
    }

    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      this.removeEventListener('mouseenter', this.pause);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('focusin', this.pause);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.removeEventListener('touchstart', this.pause);
      this.removeEventListener('touchend', this.resume);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      this.initialized = false;
    }

    start() {
      this.stop();
      if (!this.visible || this.interacting || document.hidden || this.contains(document.activeElement)) return;
      this.timer = window.setTimeout(() => this.advance(), this.delay);
    }

    stop() {
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    advance() {
      if (this.slider.scrollWidth > this.slider.clientWidth + 1) {
        if (this.nextButton.disabled) {
          this.slider.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.nextButton.click();
        }
      }
      this.start();
    }
  }

  customElements.define('rb-slider-autoplay', RbSliderAutoplay);
}
